/**
 * Shared billing formatting utilities
 * Per-Use Billing: Extracted for consistency across billing components
 *
 * Date/number/currency formatting follows the active UI language so it switches
 * with the user's language choice. The locale mapping (pl -> pl-PL, en -> en-GB)
 * mirrors the backend claims-PDF formatter for cross-surface consistency.
 */

import i18n from "@/locales";

/**
 * Low balance threshold in credits
 * Below this threshold, a warning is shown to the user
 */
export const LOW_BALANCE_THRESHOLD = 10;

/** Map the active i18next UI language to an Intl locale. */
function activeLocale(): string {
  return i18n.language?.startsWith("en") ? "en-GB" : "pl-PL";
}

/**
 * Format a minor-unit amount (grosze/cents) as currency in the active UI locale.
 *
 * `currency` is an ISO 4217 code (e.g. "PLN", "EUR"). Always prefer passing the
 * catalog / price / subscription entry's own currency (upper-cased) so the
 * display matches the actual charge currency. The fallback is "PLN" (Vardic is a
 * PLN-first product); it applies only when a caller has no currency to pass.
 */
export function formatCurrency(cents: number, currency = "PLN"): string {
  return new Intl.NumberFormat(activeLocale(), {
    style: "currency",
    currency,
  }).format(cents / 100);
}

/**
 * Format date in the active UI locale.
 * (Name retains the legacy `Pl` suffix; behavior now follows the UI language.)
 */
export function formatDatePl(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString(activeLocale(), {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/**
 * Format date with time in the active UI locale.
 * Used for credit balance last updated timestamps.
 */
export function formatDateTimePl(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString(activeLocale(), {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Format credit balance for display in the active UI locale.
 */
export function formatBalance(units: number): string {
  return new Intl.NumberFormat(activeLocale()).format(units);
}
